import { useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { useCurrentUserContext } from "providers";
import { useLanguageTranslator } from "hooks/use-language-translator";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { Input, message, Modal, Space, Spin, Table, Tooltip } from "antd";
import { ConditionalRenderer } from "components";
import { caseStatusColors } from "pages/cases-dashboard/components/cases-table-layout/constants";
import { FileNumberSmile } from "pages/cases-detail/components/file-number-smile";
import {
  CircleTickIcon,
  CloseEyeIcon,
  EmailDetailIcon,
  EmptyCircleIcon,
  EyeIcon,
  FilesTableIcon,
  FilterIconNew,
  HideEmailIcon,
  RefreshIcon,
  SearchIcon,
  UnHideEmailIcon,
  UnLinkEmailIcon,
} from "icons";
import { CaseCard } from "./case-card";
import { EmailDetail } from "./email-detail";
import { dateTimeFormatted, getFilterArrOnSearch } from "utils/helpers";
import { UseApiCallV2, UseLazyApiCallV2 } from "hooks";
import { ConfirmationPopUp } from "pages/cases-detail/components/modals";
import { ReminderFilters } from "pages/cases-detail/components/case-reminder/reminder-filters";
import { EmailFilters } from "./emails-filters";

export const Emails = () => {
  const { user } = useCurrentUserContext();
  const API_BASE = process.env.REACT_APP_baseURL_sandbox;
  const defaultPageSize = 15;
  const { translator } = useLanguageTranslator();
  const columnsTrans = translator("casesTable.tableColumnsLabel");
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const navigate = useNavigate();
  const [openEmailDetail, setOpenEmailDetail] = useState(false);
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [message_id, setMessage_id] = useState(null);
  const [searchParams] = useSearchParams();
  const appToken = JSON.parse(searchParams.get("appToken"));
  const emailNotesTrans = translator("emailNotes");
  const [userSearch, setUserSearch] = useState("");
  const [totalEmails, setTotalEmails] = useState([]);
  const [openUnlinkPopUp, setOpenUnlinkPopup] = useState(false);
  const [totalActiveFilters, setTotalActiveFilters] = useState();
  const filtersDataInitialState = {
    hidden: false,
    assigned: "all",
    hasAttachments: "all",
  };

  const [filtersData, setFiltersData] = useState({
    ...filtersDataInitialState,
  });

  function getLengthOfActiveFilters(filtersData) {
    const arr = Object.values(filtersData);
    let total = 0;
    for (let index = 0; index < arr?.length; index++) {
      if (arr[index] !== "all") {
        total = total + 1;
      }
    }
    setTotalActiveFilters(total);
  }

  const [
    getEmails,
    { data: emailsData, isLoading: emailsDataLoading, refetch: refetchEmails },
  ] = UseLazyApiCallV2({
    method: "POST",
    url: "/prod/emails",
  });
  const [unlinkEmailCall, { isLoading: unlinkLoading }] = UseLazyApiCallV2({
    method: "POST",
    url: "/deactivate_outlook",
  });
  const [
    hideOrUnhideEmail,
    {
      data: hideOrUnhideEmailData,
      isLoading: hideOrUnhideEmailDataLoading,
      refetch: refetchhideOrUnhideEmail,
    },
  ] = UseLazyApiCallV2({
    method: "POST",
    url: "/hide_email",
  });
  const [
    callProdCallback,
    { data: prodCallbackData, isLoading: prodCallbackLoading },
  ] = UseLazyApiCallV2({
    method: "POST",
    url: "/prod/callback",
  });

  // const {
  //   data: emailsData,
  //   isLoading: emailsDataLoading,
  //   refetch: refetchEmails,
  // } = UseApiCallV2({
  //   url: "/prod/emails",
  //   method: "POST",
  //   skip: !user?.email,
  //   paramsData: {
  //     email: user?.email,
  //   },
  // });

  const columns = [
    {
      title: columnsTrans.number,
      dataIndex: "key",
      key: "key",
      render: (text, record, index) => {
        const globalIndex = ((currentPage || 1) - 1) * pageSize + index + 1;
        return index < 9 && currentPage === 1 ? `${index + 1}` : globalIndex;
      },
      width: "4%",
      align: "center",
    },
    {
      title: emailNotesTrans.name,
      dataIndex: "senderName",
      key: "senderName",
      width: "12%",
    },
    {
      title: emailNotesTrans.emailAddress,
      dataIndex: "senderEmail",
      key: "senderEmail",
      width: "15%",
      ellipsis: true,
    },
    {
      title: emailNotesTrans.subject,
      dataIndex: "subject",
      key: "subject",
      width: "15%",
      ellipsis: true,
    },

    {
      title: emailNotesTrans.dateTime,
      dataIndex: "receivedDateTime",
      key: "receivedDateTime",
      width: "8%",
      render: (value, record) => {
        return value ? dateTimeFormatted(value) : "";
      },
      sorter: (a, b) => {
        const timeA =
          a.createdAt === "None" ? 0 : moment(a.createdAt).valueOf();
        const timeB =
          b.createdAt === "None" ? 0 : moment(b.createdAt).valueOf();
        return timeA - timeB; // oldest → latest, click again for reverse
      },
    },

    {
      title: emailNotesTrans.attachments,
      dataIndex: "hasAttachments",
      key: "hasAttachments",
      width: "7%",
      align: "center",
      render: (value, record) => {
        return value ? <CircleTickIcon /> : <EmptyCircleIcon />;
      },
    },
    {
      title: columnsTrans.status,
      dataIndex: "status",
      key: "status",
      width: "7%",
      align: "center",
      render: (value, record) => {
        return (
          <div className={`${record?.assigned ? styles.statusTag : ""}`}>
            {record?.assigned ? columnsTrans.assigned : columnsTrans.unassigned}
          </div>
        );
      },
    },

    {
      title: columnsTrans.action,
      key: "action",
      width: "5%",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip
            title={
              record?.hidden ? emailNotesTrans.unhide : emailNotesTrans.hide
            }
          >
            <div
              className={styles.viewDetailsBtn}
              onClick={() =>
                handleClickHide({
                  purpose: record?.hidden ? "unhide" : "hide",
                  message_id: record?.id,
                })
              }
            >
              {record?.hidden ? (
                <UnHideEmailIcon height={15} width={15} />
              ) : (
                <HideEmailIcon height={15} width={15} />
              )}
            </div>
          </Tooltip>
          <Tooltip title={emailNotesTrans.viewEmailDetail}>
            <div
              style={{ display: "flex", gap: "5px" }}
              onClick={() => {
                setMessage_id(record?.id);
                setOpenEmailDetail(true);
              }}
            >
              <button className={styles.viewDetailsBtn}>
                <EyeIcon height={15} width={15} />
              </button>
            </div>
          </Tooltip>
        </Space>
      ),
    },
  ];

  function setInitialStateOfFilters() {
    setFiltersData({
      assigned: "all",
      hasAttachments: "all",
      hidden: false,
    });
  }

  async function handleClickHide({ purpose, message_id }) {
    try {
      const res = await hideOrUnhideEmail({
        params: {
          message_id,
          purpose,
        },
      });
      message.success(res?.message);
      setFiltersData({ ...filtersDataInitialState });
      await fetchEmails();
      getLengthOfActiveFilters(filtersDataInitialState);
    } catch (error) {}
  }

  useEffect(() => {
    handleCallback();
  }, []);

  useEffect(() => {
    if (appToken && user?.email) {
      fetchEmails();
    }
  }, [user?.email]);

  useEffect(() => {
    getFilteredData(userSearch);
  }, [userSearch]);

  async function handleApplyFilters() {
    let filtered = [...totalEmails];
    if (filtersData?.hasAttachments !== "all") {
      filtered = totalEmails?.filter(
        (item) => item.hasAttachments === filtersData?.hasAttachments,
      );
    }
    if (filtersData?.assigned !== "all") {
      filtered = filtered?.filter(
        (item) => item.assigned === filtersData?.assigned,
      );
    }
    if (filtersData?.hidden !== "all") {
      filtered = filtered?.filter(
        (item) => item.hidden === filtersData?.hidden,
      );
    }

    setEmails([...filtered]);
    getLengthOfActiveFilters(filtersData);
  }

  function getFilteredData(userSearch) {
    if (!userSearch) {
      handleApplyFilters();
      return;
    }

    const search = userSearch.toLowerCase();

    const searchableFields = ["senderEmail", "senderName", "subject"];

    const filtered = getFilterArrOnSearch({
      searchableFields,
      searchValue: search,
      data: [...emails],
    });

    setEmails([...filtered]);
  }

  async function fetchEmails() {
    try {
      const res = await getEmails({
        params: {
          email: user?.email,
        },
      });
      const data = res?.body?.value;
      setTotalEmails([...data]);
      const filterHiddenEmails = data?.filter((item) => !item.hidden);
      setEmails([...filterHiddenEmails] || []);
    } catch (error) {
      console.log({ error });
    }
  }
  useEffect(() => {
    if (totalEmails?.length > 0) {
      setEmails([...totalEmails]);
    } else {
      const filterHiddenEmails = totalEmails?.filter((item) => !item.hidden);

      setEmails([...filterHiddenEmails] || []);
    }
  }, []);

  // ------------------- CALLBACK HANDLER -------------------
  const handleCallback = async () => {
    if (!appToken) {
      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.has("code")) return;

      const code = urlParams.get("code");
      const code_verifier = localStorage.getItem("pkce_verifier");
      const params = {
        code,
        code_verifier,
        loggedUserEmail: user?.email,
      };
      const res = await callProdCallback({
        params,
      });
      if (res?.body?.access_token) {
        fetchEmails();
      }
    }
  };

  function handlePagination(data) {
    setPageSize(data?.pageSize);
    setCurrentPage(data.current);
  }

  function handleCloseEmailDetail() {
    setMessage_id(null);
    setOpenEmailDetail(false);
  }

  async function onConfirmUnlinkEmail() {
    try {
      const res = await unlinkEmailCall({
        params: {
          email: user?.email,
        },
      });

      if (res?.status === "Success") {
        navigate("/cases");
      }
    } catch (error) {
      message.error("Something went wrong");
      console.log({ error });
    }
  }

  return (
    <Spin spinning={emailsDataLoading || hideOrUnhideEmailDataLoading}>
      <div className={`${styles.tableSwitchContainer} `}>
        <div className={styles.header}>
          <h3>{translator("casesTable.overview")}</h3>
          <p>{translator("casesTable.desc")}</p>
        </div>
        <div className={styles.searchAndRefresh}>
          <Input
            type="text"
            placeholder={translator("Search")}
            style={{ width: "346px", height: "39px" }}
            prefix={<SearchIcon height={15} width={15} />}
            onChange={(e) => setUserSearch(e.target.value)}
            maxLength={40}
          />
          <Tooltip title={emailNotesTrans.refreshEmails}>
            <div onClick={fetchEmails} className={styles.refreshIcon}>
              <RefreshIcon />
            </div>
          </Tooltip>
          <Tooltip title={emailNotesTrans.unlinkEmail}>
            <div
              onClick={() => setOpenUnlinkPopup(true)}
              className={styles.refreshIcon}
            >
              <UnLinkEmailIcon />
            </div>
          </Tooltip>
          <div
            className={styles.filterIcon}
            onClick={() => setShowFiltersPopup(true)}
          >
            <FilterIconNew />

            <div className={styles.number}>{totalActiveFilters}</div>
          </div>
        </div>
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.tableWrapper}>
          <Table
            onChange={handlePagination}
            columns={columns}
            dataSource={emails}
            rowClassName={styles.row}
            scroll={{
              y:
                window.innerWidth > 1600
                  ? `calc(100dvh - 255px)`
                  : `calc(100dvh - 230px)`,
            }} // Set the width for horizontal scrolling
            style={{ width: "100%", minHeight: "calc(100dvh - 145px)" }}
            className={styles.userListTable}
            sticky={true}
            pagination={{
              position: ["bottomCenter"],
              pageSize: pageSize,
              current: currentPage,
            }}
          />
        </div>
      </div>

      <Modal
        destroyOnClose
        // title="Set Reminder"
        open={openEmailDetail}
        onCancel={handleCloseEmailDetail}
        footer={null}
        centered
        width="95dvw"
        styles={{
          content: {
            backgroundColor: "#F5F6F8",
            height: "95dvh",
            padding: "0",
          },
          header: { backgroundColor: "#F5F6F8" }, // turns the Modal red
        }}
      >
        <EmailDetail message_id={message_id} refetchEmails={fetchEmails} />
      </Modal>

      <ConfirmationPopUp
        open={openUnlinkPopUp}
        onClose={() => setOpenUnlinkPopup(false)}
        Icon={UnLinkEmailIcon}
        title={emailNotesTrans.unlinkEmail}
        description={emailNotesTrans.unlinkDescription}
        onConfirm={onConfirmUnlinkEmail}
        loading={unlinkLoading}
      />
      <Modal
        title={translator("selectFilters")}
        destroyOnClose
        open={showFiltersPopup}
        onCancel={() => setShowFiltersPopup(false)}
        footer={null}
        centered
        width="45dvw"
        styles={{
          content: {
            backgroundColor: "#F5F6F8",
            borderRadius: "20px",
            minHeight: "250px",
            overflowY: "auto",
          },
          header: { backgroundColor: "#F5F6F8" }, // turns the Modal red
        }}
      >
        <EmailFilters
          filtersData={filtersData}
          setFiltersData={setFiltersData}
          setInitialStateOfFilters={setInitialStateOfFilters}
          setEmails={setEmails}
          totalEmails={totalEmails}
          handleApplyFilters={handleApplyFilters}
        />
      </Modal>
    </Spin>
  );
};
